/**
 * SpotModeUI
 * 
 * @atomic - Handles Spot Mode banner and panel UI
 * @idempotent - Pure component with React.memo
 * @modular - Self-contained spot mode interface
 * 
 * Purpose: Provides complete spot mode interface (banner + panel)
 * Props: Spot mode state, handlers, and callbacks
 * Returns: Banner + panel for spot mode workflow
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import SpotModeBanner from './SpotModeBanner'
import SpotModePanel from './SpotModePanel'
import SpotModeCrosshair from './SpotModeCrosshair'
import ListingSpotPanel from '../listings/ListingSpotPanel'
import { useMapSpot } from '../../context/MapSpotContext'

const SpotModeUI = memo(function SpotModeUI({
  isActive,
  location,
  onLocationSet,
  onCancel,
  onSubmit,
  
  // Entity selection
  entity,
  onEntityChange,
  entityType,
  onEntityTypeChange,
  entities,
  
  // Additional info
  notes,
  onNotesChange,
  entityStatus = null,
  onEntityStatusChange = null,
  socials,
  onSocialsChange,
  images,
  onImagesChange,
  
  // State
  isSubmitting,
  isPanelMinimized,
  onPanelMinimizeToggle,

  // User
  user,
  isAuthenticated,

  // Auth-deferred submit
  pendingAuthSubmit = false,

  // Layout mode
  isImmersiveMode = false,

  // GPS detecting state (GPS not yet resolved after first permission grant)
  isLocatingGPS = false,

  // Listing mode callback — called with the new listing on successful submit
  onListingCreated = null,
}) {
  const { t } = useTranslation()
  const { spotVariant } = useMapSpot()

  if (!isActive) return null

  return (
    <>
      {/* Top Banner — step progress + cancel */}
      <SpotModeBanner
        t={t}
        spotModeLocation={location}
        spotModeEntity={entity}
        spotModeNotes={notes}
        spotModeImages={images}
        spotModeStartTime={null}
        onCancel={onCancel}
        pendingAuthSubmit={pendingAuthSubmit}
        variant={spotVariant}
      />

      {/* Crosshair — visible until user drops first pin */}
      <SpotModeCrosshair visible={!location} />

      {/* After pin is dropped: show listing form OR spot entity form */}
      {spotVariant === 'listing' ? (
        location && (
          <ListingSpotPanel
            location={location}
            onCancel={onCancel}
            onCreated={onListingCreated}
            isImmersiveMode={isImmersiveMode}
          />
        )
      ) : (
        (location || isLocatingGPS) && (
          <SpotModePanel
            t={t}
            location={location}
            entity={entity}
            onEntityChange={onEntityChange}
            entityType={entityType}
            onEntityTypeChange={onEntityTypeChange}
            entities={entities}
            notes={notes}
            onNotesChange={onNotesChange}
            entityStatus={entityStatus}
            onEntityStatusChange={onEntityStatusChange}
            socials={socials}
            onSocialsChange={onSocialsChange}
            images={images}
            onImagesChange={onImagesChange}
            onSubmit={onSubmit}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
            isMinimized={isPanelMinimized}
            onMinimizeToggle={onPanelMinimizeToggle}
            user={user}
            isAuthenticated={isAuthenticated}
            isImmersiveMode={isImmersiveMode}
            isLocatingGPS={isLocatingGPS}
          />
        )
      )}
    </>
  )
})

export default SpotModeUI
